import getSearchSuggestions from "lib/getSearchSuggestionsNew";
import { useRouter, useSearchParams } from "next/navigation";
import React, { useEffect, useState } from "react";
import useDebounce from "react-use/lib/useDebounce";
import useAsyncFn from "react-use/lib/useAsyncFn";
import CRLogoLoader from "components/svg/CogRoundelLoader";
import logEvent from "lib/ga/miscEvent";
import logNewSearchSubmit from "lib/ga/newSearchSubmit";
import GoogleSearchInput from "./GoogleSearchInputWithSearchButton";
import { APIProvider } from '@vis.gl/react-google-maps';

interface Props {
  title: string,
  subTitle?: string,
  containerMargin?: string
}

type suggestion = {
  name: string,
  id: number,
  uri: string
}

import styles from './SearchGoogle.module.css'

const getSchemeItemUri = (item: suggestion) => `/scheme/${item.uri.toLocaleLowerCase()}-${item.id}`

const GoogleSearch = ({ searchType = 'PROPERTY', border = false }) => {

  const [type, setType] = useState(searchType);
  const [forSale, setForSale] = useState(false);
  const [criteria, setCriteria] = useState('LOCATION');
  const [inputValue, setInputValue] = useState('');
  const [activeSuggestion, setActiveSuggestion] = useState(0);
  //for scheme name search
  const [suggestions, setSuggestions] = useState<suggestion[]>([]);
  const [nameUri, setNameUri] = useState<string | null>(null);

  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);


  let router = useRouter();
  let query = useSearchParams();

  useEffect(() => {
    const searchType = query?.get('searchType');

    if (searchType) {
      setType(searchType === "flexible" ? "POPUP" : searchType.toUpperCase());
    }

  }, []);

  //for scheme name redirect
  useEffect(() => {

    if (nameUri) {
      setIsRedirecting(true);

      logEvent({
        category: "Search",
        action: "User made a search",
        label: nameUri,
      });
      logNewSearchSubmit({
        type: type,
        category: "Search",
        criteria: criteria,
        url: nameUri,
      });

      router.push(nameUri);
    } else {
      setIsRedirecting(false)
    }

  }, [nameUri]);

  const [suggestionsState, fetchSuggestions] = useAsyncFn(
    async () => {
      if (!inputValue) return;

      setIsSearching(true);

      const newSuggestions = await getSearchSuggestions(inputValue, 'scheme');

      setSuggestions(newSuggestions);
      setIsSearching(false);
    },
    // Variables need to be defined here in order to be used in the async function. If not defined they will exist with their initial value.
    [inputValue]
  );
  const sendQuery = () => {
    // make async call for suggestions
    fetchSuggestions();
  };

  const [debounceState, cancelDebounce] = useDebounce(
    // The fucntion to call after the debounce period

    () => {
      if (!isRedirecting) {
        sendQuery();
      }

    },
    // The period we wait
    500,
    // The depenndencies whose changes we react to to trigger the debounce. (Same as useEffect)
    [inputValue, isRedirecting]
  );
  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_APIS_KEY_CSR ?? '';

  const handleNameSearch = () => {

    if (suggestions && suggestions.length > 0) {
      const item = suggestions[0];
      setNameUri(getSchemeItemUri(item));
      setInputValue(item.name);
      setIsRedirecting(true);
    }


  }

  return (
    <div className={`${styles.SearchContainer} ${border ? styles.searchBorder : ''}`}>
      <section className={styles.SearchChangeWrapper}>

        {type == "SCHEME" && <div className={styles.CriteriaTabs}>
          <button
            onClick={(e) => {
              e.preventDefault();
              setCriteria("LOCATION");
            }}
            id="schemeByLocationTab"
            className={`${styles.SearchButton} ${criteria === "LOCATION" ? styles.active : ""}`}
          >
            Location
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              setCriteria("NAME");
            }}
            id="schemeByNameTab"
            className={`${styles.SearchButton} ${criteria === "NAME" ? styles.active : ""}`}
          >
            Name
          </button>
        </div>}

        {type == "PROPERTY" && <div className={styles.CriteriaTabs}>
          <button
            onClick={(e) => {
              e.preventDefault();
              setForSale(false);
            }}
            id="locationForRent"
            className={`${styles.SearchButton} ${!forSale ? styles.active : ""}`}
          >
            For Rent
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              setForSale(true);
            }}
            id="locationForSale"
            className={`${styles.SearchButton} ${forSale ? styles.active : ""}`}
          >
            For Sale
          </button>
        </div>}

      </section>

      <section className={styles.section}>
        <div className={styles.InputContainer}>
          <APIProvider apiKey={apiKey} onLoad={() => setIsLoaded(true)}>
            {criteria == "LOCATION" && isLoaded &&
              <GoogleSearchInput type={type} forSale={forSale} />
            }

            {criteria == "LOCATION" && !isLoaded &&
              <>
                <input id="google-search-input" type="text" placeholder="Search by city, town, street or postcode" autoComplete="off" />
                <button className={styles.googleSearchButton}>Search</button>
              </>
            }

          </APIProvider>
          {criteria == "NAME" &&
            <div className={styles.InputContainer}>
              <input
                id="name-search"
                value={inputValue}
                type="text"
                placeholder="Begin typing the name of the retail scheme"
                autoComplete="off"
                onChange={(e) => {
                  setInputValue(e.target.value);
                  setActiveSuggestion(0)
                }}
                onKeyDown={(e) => {
                  if (e.key == "ArrowDown") {
                    let newIndex = activeSuggestion + 1;
                    if (newIndex >= 5) {
                      newIndex = 0;
                    }
                    setActiveSuggestion(newIndex);
                  }
                  if (e.key == "ArrowUp") {
                    let newIndex = activeSuggestion - 1;
                    if (newIndex < 0) {
                      newIndex = 4;
                    }
                    setActiveSuggestion(newIndex);
                  }
                  if (e.key == "Enter") {
                    let item = suggestions[activeSuggestion];
                    // CR-1369
                    if (item) {
                      setNameUri(getSchemeItemUri(item));
                      setInputValue(item.name);
                      setIsRedirecting(true);
                    }
                  }
                }}
                disabled={isRedirecting}
              />
              <button className={styles.googleSearchButton} onClick={() => { handleNameSearch() }}>Search</button>
            </div>
          }


          {criteria == "NAME" && <ul className={styles.Suggestions} >
            {suggestions.length > 0 && !isRedirecting &&
              suggestions.map((item, index) => (
                <li className={styles.Suggestion}
                  key={`${item.id}${index}`}
                  onClick={() => {
                    setNameUri(getSchemeItemUri(item));
                    setInputValue(item.name);
                    setIsRedirecting(true);
                  }}
                >
                  {item.name}
                </li>
              ))}
          </ul>}
        </div>
      </section>
    </div>
  )
}

export default GoogleSearch;
